import { useTranslation } from 'react-i18next';
import './AvailableCertifications.scss';
import Subtitle from 'components/TextComps/Subtitle';
import Paragraph from 'components/TextComps/Paragraph';
import { mockCertificates } from 'pages/SubscriptionsAndPricing/mockCertificates';
import { useCartStore } from 'stores/SubscriptionsAndPricing/CartStore';
import { ShopCertificate } from 'types/pricing';
import { useQuery } from '@tanstack/react-query';
import { useServices } from 'index';
import { useCallback, useMemo } from 'react';
import certLevel1Icon from 'images/Certification/cert_lvl_1.png';
import certLevel2Icon from 'images/Certification/cert_lvl_2.png';
import { ReactComponent as Plus } from 'images/icons/Plus.svg';
import { IconButton } from '@trackman/web-shared-components';
import { CartItem } from 'types/cart';
import { AddedToCart } from 'pages/SubscriptionsAndPricing/AddedToCart/AddedToCart';
import { addCartItemToLocalStorage } from 'utilities/cartLocalStorageHelper';

export const AvailableCertifications = () => {
  const { t } = useTranslation();

  const profileService = useServices().profileService;
  const { data: isPremium, isPending } = useQuery({
    queryKey: ['isUserMember'],
    queryFn: profileService.checkIfUserIsMember,
    refetchOnWindowFocus: false,
  });

  const certificates = mockCertificates;
  const itemsInCart = useCartStore((state) => state.items);
  const addToCart = useCartStore((state) => state.addItemToCart);

  const availableCertificates = useMemo(() => {
    return certificates.filter((cert) => cert.status === 'unlocked');
  }, [certificates, itemsInCart]);

  const hasDiscount = useMemo(() => {
    return (!isPending && isPremium) || itemsInCart.some((item) => item.id === 'premium');
  }, [isPremium, isPending, itemsInCart]);

  const getAreAllCertificatesInCart = useMemo(() => {
    return availableCertificates.every((certificate) => itemsInCart.some((item) => item.id === certificate.id));
  }, [availableCertificates, itemsInCart]);

  const getIsItemInCart = useCallback(
    (certificateId: string) => {
      return itemsInCart.some((item) => item.id === certificateId);
    },
    [itemsInCart]
  );

  const handleAddToCart = useCallback(
    (certificate: CartItem) => {
      addToCart(certificate);
      addCartItemToLocalStorage(certificate);
    },
    [addToCart]
  );

  return (
    <div className='available-certifications'>
      {getAreAllCertificatesInCart ? (
        <></>
      ) : (
        <div className='available-certifications-header'>
          <Subtitle>
            <>{t('WouldYouLikeToAddSomethingElse', { ns: 'subscriptionsAndPricing' })}</>
          </Subtitle>
          <Paragraph>{t('AddAnotherCertification', { ns: 'subscriptionsAndPricing' })}</Paragraph>
        </div>
      )}

      <div className='certifications-list'>
        {availableCertificates.map((certificate) => (
          <CertificationCard
            key={certificate.id}
            certificate={certificate}
            isItemInCart={getIsItemInCart(certificate.id)}
            hasDiscount={hasDiscount}
            discountedPrice={hasDiscount ? certificate.price * 0.5 : undefined}
            addToCart={(certificate: CartItem) => handleAddToCart(certificate)}
          />
        ))}
      </div>
    </div>
  );
};

const CertificationCard = ({
  certificate,
  hasDiscount,
  isItemInCart,
  discountedPrice,
  addToCart,
}: {
  certificate: ShopCertificate;
  hasDiscount: boolean;
  isItemInCart: boolean;
  discountedPrice?: number;
  addToCart: (certificate: CartItem) => void;
}) => {
  const { t } = useTranslation();

  const getCertificationLevel = useMemo(
    () =>
      certificate.title.toLowerCase().includes('level 1') ? 'level1' : certificate.title.toLowerCase().includes('level 2') ? 'level2' : '',
    [certificate.title]
  );

  return (
    <div className='certification-card'>
      <div className='certification-card-header'>
        <img
          src={certificate.badgeImageUrl ? certificate.badgeImageUrl : getCertificationLevel === 'level1' ? certLevel1Icon : certLevel2Icon}
          alt={certificate.title ?? ''}
        />
        {!isItemInCart ? (
          <IconButton
            icon={() => <Plus />}
            intent='secondary'
            onClick={() =>
              addToCart({
                id: certificate.id,
                title: certificate.title,
                basePrice: certificate.price,
                discountedPrice: hasDiscount ? certificate.price * 0.5 : undefined,
              })
            }
          />
        ) : (
          <AddedToCart />
        )}
      </div>
      <div className='certification-card-body'>
        <Subtitle>
          <>{certificate.title}</>
        </Subtitle>
        <Paragraph>
          <>{certificate.shortDescription}</>
        </Paragraph>
        <div>
          <span>{hasDiscount ? `$${discountedPrice}` : `$${certificate.price}`}</span>
          <span className={hasDiscount ? 'base-price' : 'get-discount'}>
            {hasDiscount ? `$${certificate.price}` : t('Get50%Off', { ns: 'subscriptionsAndPricing' })}
          </span>
        </div>
      </div>
    </div>
  );
};
